import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, Navigation } from "lucide-react";
import { FaWhatsapp, FaInstagram, FaFacebookF, FaYoutube, FaTripadvisor, FaGoogle } from "react-icons/fa";
import { SITE, waLink, socials } from "../data/site";
import { useLang } from "../i18n";

const COLS = [
  {
    key: "fServices",
    links: [
      ["Local Jodhpur Taxi", "/local-jodhpur-taxi"],
      ["Airport Taxi", "/airport-taxi"],
      ["One Way Taxi", "/one-way-taxi"],
      ["Outstation Taxi", "/outstation-taxi"],
      ["My Fleet", "/fleet"],
      ["All Services", "/services"],
    ],
  },
  {
    key: "fCities",
    links: [
      ["Taxi in Jodhpur", "/taxi-service-jodhpur"],
      ["Taxi in Jaipur", "/taxi-service-jaipur"],
      ["Taxi in Udaipur", "/taxi-service-udaipur"],
      ["Taxi in Jaisalmer", "/taxi-service-jaisalmer"],
      ["Taxi in Mount Abu", "/taxi-service-mount-abu"],
      ["Taxi in Ajmer", "/taxi-service-ajmer"],
      ["Taxi in Pushkar", "/taxi-service-pushkar"],
      ["Taxi in Bikaner", "/taxi-service-bikaner"],
    ],
  },
  {
    key: "fRoutes",
    links: [
      ["Jodhpur to Jaipur Taxi", "/one-way-taxi"],
      ["Jodhpur to Udaipur Taxi", "/one-way-taxi"],
      ["Jodhpur to Jaisalmer Taxi", "/one-way-taxi"],
      ["Jodhpur to Mount Abu Taxi", "/one-way-taxi"],
      ["Tour Packages", "/tour-packages"],
      ["Travel Blog", "/blog"],
      ["All Rajasthan Taxi", "/all-rajasthan-taxi"],
    ],
  },
];

const SOCIALS = [
  [FaWhatsapp, "WhatsApp", socials.whatsapp],
  [FaInstagram, "Instagram (coming soon)", socials.instagram],
  [FaFacebookF, "Facebook (coming soon)", socials.facebook],
  [FaYoutube, "YouTube (coming soon)", socials.youtube],
  [FaTripadvisor, "Tripadvisor (coming soon)", socials.tripadvisor],
  [FaGoogle, "Google Business Profile", socials.google],
];

export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-navy text-white relative overflow-hidden grain">
      <div className="gold-line absolute top-0 left-0 right-0 opacity-70" />
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3">
              <div className="bg-white rounded-xl p-1.5">
                <img src="/logo.jpeg" alt="Blue City Taxi logo" className="h-11 w-auto rounded-lg" />
              </div>
              <span className="leading-none">
                <span className="block font-heading font-extrabold text-xl tracking-tight text-white">
                  BLUE CITY <span className="text-gold">TAXI</span>
                </span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.24em] text-white/50 mt-1">
                  Jodhpur · Rajasthan
                </span>
              </span>
            </div>
            <p className="mt-5 text-sm text-white/70 leading-relaxed max-w-sm">{t("fBlurb")}</p>
            <address className="mt-5 not-italic rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="footer-address">
              <p className="flex items-start gap-2.5 text-sm text-white/80">
                <MapPin size={16} className="text-gold mt-0.5 shrink-0" />
                <span>{SITE.address1}<br />{SITE.address2}</span>
              </p>
              <a
                href={SITE.googleProfile}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-directions-link"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-gold hover:text-white transition-colors"
              >
                <Navigation size={12} /> Get Directions on Google Maps
              </a>
            </address>
            <div className="mt-4 space-y-2.5 text-sm">
              <a href={`tel:${SITE.phoneTel}`} className="flex items-center gap-2.5 text-white/85 hover:text-gold transition-colors" data-testid="footer-phone">
                <Phone size={15} className="text-gold" /> {SITE.phoneDisplay}
              </a>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-white/85 hover:text-gold transition-colors" data-testid="footer-whatsapp">
                <FaWhatsapp size={15} className="text-gold" /> {t("whatsappBooking")}
              </a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-2.5 text-white/85 hover:text-gold transition-colors" data-testid="footer-email">
                <Mail size={15} className="text-gold" /> {SITE.email}
              </a>
            </div>
            <div className="mt-6 flex gap-2.5">
              {SOCIALS.map(([Icon, label, href], i) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold/50 hover:-translate-y-0.5 transition-all"
                  aria-label={label}
                  data-testid={`footer-social-${i}`}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>
          {COLS.map((c) => (
            <nav key={c.key} aria-label={c.key}>
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">{t(c.key)}</h2>
              <ul className="space-y-2.5">
                {c.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} className="text-sm text-white/70 hover:text-white transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} Blue City Taxi, Jodhpur, Rajasthan. All rights reserved.</p>
          <p>Taxi Service in Jodhpur · One Way Taxi · Outstation Cabs · Rajasthan Tours</p>
        </div>
      </div>
    </footer>
  );
}
